import {
  Box,
  BoxProps,
  Button,
  Image,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import * as React from 'react';

import { GlobalSearch } from '@/components/GlobalSearch';
import { AIAsk } from '@/components/GlobalSearch/AIAsk';
import { EPromptState, LogInSignUpModal } from '@/components/LogInSignUp';

import { useCurrent } from '@/hooks/useCurrent';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { usePermissions } from '@/hooks/usePermissions';
import { useWebflowNavChrome } from '@/hooks/useWebflowNavChrome';
import { AccountMenu } from '@/imports/ui/components/community-frame/AccountMenu';
import { ChatNavLinkIconDisplay } from '@/imports/ui/components/community-frame/chat-nav/ChatNavLinkIconDisplay';
import { CartNavLinkIconDisplay } from '@/imports/ui/components/community-frame/CartNavLinkIconDisplay';
import { TenantSearchMode } from '@/imports/ui/components/search/utils';
import { InAppNotificationDropdown } from '@/imports/ui/notifications/components';
import { webflowNavHtmlHasGuestAuthControls } from '@/lib/webflow-poc/guest-auth-nav-links';
import { preservesWebflowNavStyles } from '@/lib/webflow-poc/navigation-chrome';
import { WebflowNavAlignedUtilities } from '@/lib/webflow-poc/WebflowNavAlignedUtilities';
import { WebflowNavChrome } from '@/lib/webflow-poc/WebflowNavChrome';
import { showChatIconInMobile } from '@/utilities';

import { tokenStorage } from '../../../../../modules/accounts/tokenStorage';
import { HeaderNavigationMenu, HeaderNavigationTabs } from './HeaderNavigation';
import { EAccountCreationPermission } from '@/graphql-types/globalTypes';

export const Header: React.FC<{ isContained?: boolean } & BoxProps> = ({
  isContained = true,
  ...props
}) => {
  const memberManagement = useMemberManagement();
  const loginModal = useDisclosure();
  const aiAskDrawer = useDisclosure();
  const current = useCurrent();
  const [loginModalPromptState, setLoginModalPromptState] =
    React.useState<EPromptState>(EPromptState.LOGIN_FORM);
  const permissions = usePermissions();
  const router = useRouter();
  const pathName = usePathname();

  const showChatIcon = showChatIconInMobile();
  const [hasPreviousPage, setHasPreviousPage] = React.useState(false);
  const webflowNav = useWebflowNavChrome();
  // Only when feature flag + integrated + WEBFLOW/WEBFLOW_NATIVE chrome.
  // configuredForWebflowNav is false otherwise → standard TW header/CSS below.
  const useWebflowHeader =
    webflowNav.available ||
    (webflowNav.configuredForWebflowNav && webflowNav.loading);

  React.useEffect(() => {
    const previousPath = sessionStorage.getItem('initialPath');
    if (
      previousPath === null ||
      previousPath === pathName ||
      pathName === '/' ||
      pathName === '/login'
    ) {
      setHasPreviousPage(false);
    } else {
      setHasPreviousPage(true);
    }

    if (previousPath === null) {
      sessionStorage.setItem('initialPath', pathName);
    }
  }, [pathName]);

  const handleGoBack = () => {
    if (hasPreviousPage) {
      router.back();
    }
  };

  const webflowChromeRef = React.useRef<HTMLDivElement>(null);

  const guestActions = (
    <>
      <Button
        onClick={() => {
          setLoginModalPromptState(EPromptState.LOGIN_FORM);
          loginModal.onOpen();
        }}
        colorScheme="gray"
      >
        Log in
      </Button>

      {memberManagement.isActive ? (
        <NextLink href="/memberships">
          <Button colorScheme="primary">Join Now</Button>
        </NextLink>
      ) : current.tenant?.accountCreationPermission ===
        EAccountCreationPermission.ANYONE ? (
        <Button
          onClick={() => {
            setLoginModalPromptState(EPromptState.SIGNUP_FORM);
            loginModal.onOpen();
          }}
          colorScheme="primary"
        >
          Sign up
        </Button>
      ) : null}

      <LogInSignUpModal
        initialPromptState={loginModalPromptState}
        isOpen={loginModal.isOpen}
        onClose={loginModal.onClose}
      />
    </>
  );

  const authenticatedUtilityActions = (
    <>
      {permissions.canAccessCommunityAndAuthenticated &&
        !tokenStorage.getItem('isMobile') && (
          <Box onClickCapture={aiAskDrawer.onClose}>
            <GlobalSearch />
          </Box>
        )}

      {permissions.canAccessCommunityAndAuthenticated &&
        !tokenStorage.getItem('isMobile') &&
        current.tenant?.searchMode === TenantSearchMode.AI &&
        current.tenant?.featureFlags?.aiAskEnabled === true && (
          <AIAsk
            isOpen={aiAskDrawer.isOpen}
            onOpen={aiAskDrawer.onOpen}
            onClose={aiAskDrawer.onClose}
          />
        )}

      <Stack direction="row" spacing="4" onClickCapture={aiAskDrawer.onClose}>
        {!tokenStorage.getItem('isMobile') && <InAppNotificationDropdown />}

        {permissions.canAccessDmPageAndAuthenticated && showChatIcon && (
          <ChatNavLinkIconDisplay />
        )}

        {!tokenStorage.getItem('isMobile') && <CartNavLinkIconDisplay />}
        {!tokenStorage.getItem('isMobile') && <AccountMenu />}
      </Stack>
    </>
  );

  const accountActions = (
    <>
      {permissions.isLoading ? null : permissions.isAuthenticated ? (
        useWebflowHeader ? null : authenticatedUtilityActions
      ) : (
        guestActions
      )}
    </>
  );

  if (useWebflowHeader) {
    const webflowHasGuestAuth = webflowNavHtmlHasGuestAuthControls(
      webflowNav.nav?.html
    );
    // Prefer Webflow Login / Sign up when present; only show TW guest buttons as fallback.
    const showGuestAside =
      !permissions.isLoading &&
      !permissions.isAuthenticated &&
      !webflowHasGuestAuth;
    const keepWebflowCss = preservesWebflowNavStyles(
      webflowNav.navigationChrome
    );

    return (
      <Box
        bg={keepWebflowCss ? 'transparent' : 'white'}
        shadow={keepWebflowCss ? 'none' : 'base'}
        as="header"
        {...props}
      >
        <Stack
          direction="row"
          maxW={
            keepWebflowCss
              ? undefined
              : isContained
              ? 'container.xl'
              : undefined
          }
          mx={keepWebflowCss ? undefined : 'auto'}
          alignItems="center"
          justify="space-between"
          px={keepWebflowCss ? '0' : '4'}
          spacing="4"
          w="full"
        >
          {tokenStorage.getItem('isMobile') && hasPreviousPage && (
            <Button
              onClick={() => handleGoBack()}
              leftIcon={
                <Image src="/arrow-left-circle.png" alt="Back" boxSize={6} />
              }
              bg="transparent"
              _hover={{ bg: 'transparent', color: 'black' }}
              _active={{ bg: 'transparent', color: 'black' }}
              _focus={{ boxShadow: 'none' }}
              color="black"
            />
          )}

          <Box
            ref={webflowChromeRef}
            flex="1"
            // minW={0} clips/shifts Webflow absolute hover + dropdown positioning
            minW="auto"
            overflow="visible"
            py={keepWebflowCss ? '0' : '2'}
            display="flex"
            alignItems="center"
            position="relative"
          >
            {webflowNav.available && webflowNav.nav ? (
              <WebflowNavChrome
                nav={webflowNav.nav}
                preserveWebflowStyles={keepWebflowCss}
              />
            ) : null}
            {permissions.isAuthenticated ? (
              <WebflowNavAlignedUtilities
                containerRef={webflowChromeRef}
                htmlKey={webflowNav.nav?.html}
              />
            ) : null}
          </Box>

          {showGuestAside ? (
            <Stack
              direction="row"
              spacing="4"
              py="4"
              flexShrink={0}
              justifyContent="flex-end"
              alignItems="center"
            >
              {guestActions}
            </Stack>
          ) : null}
        </Stack>
      </Box>
    );
  }

  return (
    <Box bg="white" shadow="base" as="header" {...props}>
      <Stack
        direction="row"
        maxW={isContained ? 'container.xl' : undefined}
        mx="auto"
        alignItems="stretch"
        justify="space-between"
        px="4"
        spacing="4"
      >
        <Box py="4" flex="1" display="flex" alignItems="center">
          {tokenStorage.getItem('isMobile') && hasPreviousPage && (
            <Button
              onClick={() => handleGoBack()}
              leftIcon={
                <Image src="/arrow-left-circle.png" alt="Back" boxSize={6} />
              }
              bg="transparent"
              _hover={{ bg: 'transparent', color: 'black' }}
              _active={{ bg: 'transparent', color: 'black' }}
              _focus={{ boxShadow: 'none' }}
              color="black"
            />
          )}

          {current.tenant?.logo?.imageProxy &&
            !tokenStorage.getItem('isMobile') && (
              <NextLink href="/">
                <Image
                  display={{ base: 'none', lg: 'block' }}
                  maxH="10"
                  maxW="52"
                  w="auto"
                  alt="association logo"
                  src={current.tenant.logo.imageProxy}
                  cursor="pointer"
                />
              </NextLink>
            )}
          {current.tenant?.logoIcon?.imageProxy &&
            !tokenStorage.getItem('isMobile') && (
              <NextLink href="/">
                <Image
                  display={{ lg: 'none' }}
                  maxH="10"
                  maxW="10"
                  w="auto"
                  alt="association logo"
                  src={current.tenant.logoIcon.imageProxy}
                  cursor="pointer"
                />
              </NextLink>
            )}
        </Box>

        <HeaderNavigationTabs display={{ base: 'none', lg: 'flex' }} />

        <Stack
          direction="row"
          spacing="4"
          py="4"
          flex="1"
          justifyContent="flex-end"
        >
          {accountActions}

          {!tokenStorage.getItem('isMobile') && (
            <HeaderNavigationMenu
              display={{ lg: 'none' }}
              aria-label="navigation menu"
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
